import { FunctionsHttpError } from '@supabase/supabase-js';
import { fetchOsrmSegment } from './demDispatchGeocode';
import { FLEET_FEATURE_ENABLED } from './fleetFeature';
import { formatSupabaseError } from './formatSupabaseError';
import { getDbSupabase } from './supabase';
import { singaporeTodayDateString } from '../utils/dateTime';
export type CartrackVehicle = {
  registration: string;
  label: string;
  lat: number | null;
  lng: number | null;
  speedKmh: number | null;
  driverName: string | null;
  driverEmail: string | null;
  ignitionOn: boolean | null;
  updatedAt: string | null;
  odometerKm: number | null;
};

export type CartrackRoutePoint = {
  lat: number;
  lng: number;
  at: string | null;
  speedKmh: number | null;
};

async function readFunctionsHttpError(error: FunctionsHttpError): Promise<string> {
  try {
    const response = error.context;
    if (response && typeof response.json === 'function') {
      const body = (await response.json()) as { error?: string; message?: string };
      if (typeof body.error === 'string' && body.error.trim()) return body.error;
      if (typeof body.message === 'string' && body.message.trim()) return body.message;
    }
  } catch {
    // fall through
  }
  return error.message || 'Edge Function returned a non-2xx status code';
}

async function invokeCartrack<T>(body: Record<string, unknown>): Promise<T> {
  if (!FLEET_FEATURE_ENABLED) {
    throw new Error('Fleet / Cartrack is temporarily disabled.');
  }
  const { data, error } = await getDbSupabase('crm').functions.invoke('cartrack-api', { body });
  if (error) {
    if (error instanceof FunctionsHttpError) {
      throw new Error(await readFunctionsHttpError(error));
    }
    throw new Error(formatSupabaseError(error));
  }
  if (data && typeof data === 'object' && 'error' in data && (data as { error?: string }).error) {
    throw new Error(String((data as { error: string }).error));
  }
  return data as T;
}

export async function fetchCartrackConnection(): Promise<{ connected: boolean; baseUrl: string }> {
  return invokeCartrack({ action: 'getConnection' });
}

export async function fetchCartrackVehicleStatus(): Promise<{
  vehicles: CartrackVehicle[];
  fetchedAt: string;
}> {
  return invokeCartrack({ action: 'getVehicleStatus' });
}

export async function fetchCartrackVehicleEvents(
  registration: string,
  date: string,
  options?: { forceRefresh?: boolean },
): Promise<{
  registration: string;
  date: string;
  events: CartrackRoutePoint[];
  rawEventCount?: number;
  fetchedAt: string;
  fromCache?: boolean;
}> {
  const reg = cartrackVehicleId({ registration });

  if (!options?.forceRefresh) {
    try {
      const { data, error } = await getDbSupabase('crm')
        .from('cartrack_route_cache')
        .select('events, raw_event_count, fetched_at')
        .eq('registration', reg)
        .eq('route_date', date)
        .maybeSingle();

      if (!error && data && Array.isArray(data.events) && data.events.length > 0) {
        const fetchedAt = String(data.fetched_at ?? '');
        if (isRouteCacheFresh(fetchedAt, date)) {
          return {
            registration,
            date,
            events: data.events as CartrackRoutePoint[],
            rawEventCount: Number(data.raw_event_count ?? data.events.length),
            fetchedAt,
            fromCache: true,
          };
        }
      }
    } catch {
      // Cache table may not exist yet — fall through to Edge Function.
    }
  }

  const result = await invokeCartrack<{
    registration: string;
    date: string;
    events: CartrackRoutePoint[];
    rawEventCount?: number;
    fetchedAt: string;
    fromCache?: boolean;
  }>({
    action: 'getVehicleEvents',
    registration,
    date,
    forceRefresh: options?.forceRefresh === true,
  });

  return { ...result, fromCache: result.fromCache ?? false };
}

function isRouteCacheFresh(fetchedAt: string, routeDate: string): boolean {
  const today = singaporeTodayDateString();
  if (routeDate < today) return true;
  if (routeDate > today) return false;
  const fetchedMs = Date.parse(fetchedAt);
  if (Number.isNaN(fetchedMs)) return false;
  return Date.now() - fetchedMs < 15 * 60 * 1000;
}

export function cartrackVehicleId(vehicle: Pick<CartrackVehicle, 'registration'>): string {
  return vehicle.registration.replace(/[\s-]/g, '').toUpperCase();
}

function isLikelyTruncatedRoute(events: CartrackRoutePoint[]): boolean {
  if (events.length < 50) return false;
  const times = events
    .map((point) => point.at)
    .filter((value): value is string => Boolean(value))
    .map((value) => Date.parse(value.replace(' ', 'T')))
    .filter((value) => Number.isFinite(value));
  if (times.length < 2) return false;
  const spanMs = Math.max(...times) - Math.min(...times);
  return spanMs > 0 && spanMs < 45 * 60 * 1000;
}

export type FleetRouteLayer = {
  points: CartrackRoutePoint[];
  /** Road estimate where GPS dropped (tunnel / signal loss). */
  estimated: boolean;
};

/** Read cached route from Supabase for instant map paint (no Edge Function). */
export async function readCartrackRouteCache(
  registration: string,
  date: string,
): Promise<{
  events: CartrackRoutePoint[];
  fetchedAt: string;
  layers: FleetRouteLayer[] | null;
} | null> {
  const reg = cartrackVehicleId({ registration });
  try {
    const { data, error } = await getDbSupabase('crm')
      .from('cartrack_route_cache')
      .select('events, fetched_at, route_layers, layers_event_count')
      .eq('registration', reg)
      .eq('route_date', date)
      .maybeSingle();
    if (error || !data || !Array.isArray(data.events) || data.events.length === 0) return null;
    const events = data.events as CartrackRoutePoint[];
    if (isLikelyTruncatedRoute(events)) return null;
    const fetchedAt = String(data.fetched_at ?? '');
    if (!isRouteCacheFresh(fetchedAt, date)) return null;

    const layersEventCount = Number(data.layers_event_count ?? 0);
    const rawLayers = data.route_layers;
    const layers =
      Array.isArray(rawLayers) &&
      rawLayers.length > 0 &&
      layersEventCount === events.length
        ? (rawLayers as FleetRouteLayer[])
        : null;

    return { events, fetchedAt, layers };
  } catch {
    return null;
  }
}

/** Persist OSRM/gap-filled layers so the next open can paint instantly. */
export async function saveCartrackRouteLayers(
  registration: string,
  date: string,
  layers: FleetRouteLayer[],
  eventCount: number,
): Promise<void> {
  const reg = cartrackVehicleId({ registration });
  try {
    await getDbSupabase('crm').rpc('save_cartrack_route_layers', {
      p_registration: reg,
      p_route_date: date,
      p_layers: layers,
      p_event_count: eventCount,
    });
  } catch {
    // Migration may not be applied yet — in-memory cache still works for this session.
  }
}

/**
 * Load GPS + OSRM layers: memory → disk (with saved layers) → fetch/build → save layers.
 */
export async function ensureCartrackRouteBundle(
  registration: string,
  date: string,
  options?: { forceRefresh?: boolean; memory?: Map<string, { events: CartrackRoutePoint[]; layers: FleetRouteLayer[] }> },
): Promise<{ events: CartrackRoutePoint[]; layers: FleetRouteLayer[]; fromDiskLayers: boolean }> {
  const cacheKey = `${cartrackVehicleId({ registration })}::${date}`;
  const memory = options?.memory;
  if (!options?.forceRefresh && memory?.has(cacheKey)) {
    const hit = memory.get(cacheKey)!;
    return { ...hit, fromDiskLayers: true };
  }

  if (!options?.forceRefresh) {
    const disk = await readCartrackRouteCache(registration, date);
    if (disk?.events.length) {
      if (disk.layers?.length) {
        const bundle = { events: disk.events, layers: disk.layers };
        memory?.set(cacheKey, bundle);
        return { ...bundle, fromDiskLayers: true };
      }

      const prepared = prepareRouteDisplay(disk.events);
      const layers =
        prepared.length >= 2
          ? await buildFleetRouteLayers(prepared)
          : disk.events.length
            ? [{ points: disk.events, estimated: false }]
            : [];
      const bundle = { events: disk.events, layers };
      memory?.set(cacheKey, bundle);
      void saveCartrackRouteLayers(registration, date, layers, disk.events.length);
      return { ...bundle, fromDiskLayers: false };
    }
  }

  const result = await fetchCartrackVehicleEvents(registration, date, {
    forceRefresh: options?.forceRefresh === true,
  });
  const prepared = prepareRouteDisplay(result.events);
  const layers =
    prepared.length >= 2
      ? await buildFleetRouteLayers(prepared)
      : result.events.length
        ? [{ points: result.events, estimated: false }]
        : [];
  const bundle = { events: result.events, layers };
  memory?.set(cacheKey, bundle);
  void saveCartrackRouteLayers(registration, date, layers, result.events.length);
  return { ...bundle, fromDiskLayers: false };
}

export function formatCartrackSpeed(speedKmh: number | null | undefined): string {
  if (speedKmh == null || !Number.isFinite(speedKmh)) return '—';
  return `${Math.round(speedKmh)} km/h`;
}

export function formatCartrackUpdatedAt(value: string | null | undefined): string {
  const trimmed = value?.trim();
  if (!trimmed) return '—';
  const parsed = Date.parse(trimmed.replace(' ', 'T'));
  if (Number.isNaN(parsed)) return trimmed;
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Singapore',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  }).format(new Date(parsed));
}

function haversineMeters(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const earthRadiusM = 6_371_000;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * earthRadiusM * Math.asin(Math.sqrt(a));
}

/** Drop duplicate GPS points; keep points at least minGapMeters apart for rendering. */
export function prepareRouteDisplay(points: CartrackRoutePoint[], minGapMeters = 8): CartrackRoutePoint[] {
  const filtered: CartrackRoutePoint[] = [];
  for (const point of points) {
    if (!Number.isFinite(point.lat) || !Number.isFinite(point.lng)) continue;
    const prev = filtered[filtered.length - 1];
    if (!prev) {
      filtered.push(point);
      continue;
    }
    if (haversineMeters(prev.lat, prev.lng, point.lat, point.lng) >= minGapMeters) {
      filtered.push(point);
    }
  }
  return filtered;
}

function normalizeRouteTimestamp(value: string): string {
  let normalized = value.trim();
  normalized = normalized.replace(/^(\d{4}-\d{2}-\d{2})\s+/, '$1T');
  // Cartrack often returns "+08" instead of ISO "+08:00".
  normalized = normalized.replace(/([+-]\d{2})$/, '$1:00');
  normalized = normalized.replace(/\s+([+-]\d{2}:\d{2})$/, '$1');
  return normalized;
}

function parseRouteTimestamp(value: string | null | undefined): number | null {
  if (!value) return null;
  const parsed = Date.parse(normalizeRouteTimestamp(value));
  return Number.isFinite(parsed) ? parsed : null;
}

function impliedSpeedKmh(a: CartrackRoutePoint, b: CartrackRoutePoint): number | null {
  const distM = haversineMeters(a.lat, a.lng, b.lat, b.lng);
  const t0 = parseRouteTimestamp(a.at);
  const t1 = parseRouteTimestamp(b.at);
  if (t0 == null || t1 == null || t1 <= t0) return null;
  const hours = (t1 - t0) / 3_600_000;
  return (distM / 1000) / hours;
}

/** Split route where GPS jumps (signal loss / tunnel) would draw misleading straight lines. */
export function splitRouteSegments(
  points: CartrackRoutePoint[],
  maxGapMeters = 800,
  maxImpliedSpeedKmh = 160,
): CartrackRoutePoint[][] {
  if (points.length < 2) return points.length ? [points] : [];

  const segments: CartrackRoutePoint[][] = [];
  let current: CartrackRoutePoint[] = [points[0]!];

  for (let i = 1; i < points.length; i++) {
    const prev = current[current.length - 1]!;
    const next = points[i]!;
    const gapM = haversineMeters(prev.lat, prev.lng, next.lat, next.lng);
    const speed = impliedSpeedKmh(prev, next);

    if (gapM > maxGapMeters || (speed != null && speed > maxImpliedSpeedKmh)) {
      if (current.length >= 2) segments.push(current);
      current = [next];
      continue;
    }
    current.push(next);
  }

  if (current.length >= 2) segments.push(current);
  return segments;
}

function bridgePointsBetween(
  from: CartrackRoutePoint,
  to: CartrackRoutePoint,
  geometry: [number, number][] | null,
): CartrackRoutePoint[] {
  if (geometry && geometry.length >= 2) {
    return geometry.map(([lat, lng]) => ({ lat, lng, at: null, speedKmh: null }));
  }
  return [
    { lat: from.lat, lng: from.lng, at: from.at, speedKmh: null },
    { lat: to.lat, lng: to.lng, at: to.at, speedKmh: null },
  ];
}

/** Fill GPS gaps with OSRM driving-route estimates (e.g. MCE → KPE tunnels). */
export async function buildFleetRouteLayers(
  points: CartrackRoutePoint[],
  options?: { maxGapMeters?: number; maxBridges?: number },
): Promise<FleetRouteLayer[]> {
  if (points.length < 2) {
    return points.length ? [{ points, estimated: false }] : [];
  }

  const segments = splitRouteSegments(points, options?.maxGapMeters ?? 800);
  if (segments.length <= 1) {
    return [{ points, estimated: false }];
  }

  const maxBridges = options?.maxBridges ?? 12;
  const bridgeCount = Math.min(segments.length - 1, maxBridges);
  const bridges = await Promise.all(
    Array.from({ length: bridgeCount }, async (_, i) => {
      const from = segments[i]![segments[i]!.length - 1]!;
      const to = segments[i + 1]![0]!;
      const osrm = await fetchOsrmSegment([from.lat, from.lng], [to.lat, to.lng]);
      return bridgePointsBetween(from, to, osrm?.geometry ?? null);
    }),
  );

  const layers: FleetRouteLayer[] = [];
  segments.forEach((segment, i) => {
    layers.push({ points: segment, estimated: false });
    if (i < bridges.length) {
      layers.push({ points: bridges[i]!, estimated: true });
    }
  });

  return layers;
}

export type FleetRouteStop = {
  lat: number;
  lng: number;
  startedAt: string;
  endedAt: string;
  durationMinutes: number;
  address?: string;
};

export type FleetRouteTrip = {
  startedAt: string;
  endedAt: string;
  start: { lat: number; lng: number };
  end: { lat: number; lng: number };
};

export type FleetRouteAnalysis = {
  dayStart: { lat: number; lng: number; at: string } | null;
  dayEnd: { lat: number; lng: number; at: string } | null;
  trips: FleetRouteTrip[];
  stops: FleetRouteStop[];
};

function detectStopsInSegment(
  points: CartrackRoutePoint[],
  minDurationMs: number,
  radiusM: number,
): FleetRouteStop[] {
  const stops: FleetRouteStop[] = [];
  let i = 0;

  while (i < points.length) {
    const anchor = points[i]!;
    const anchorTime = parseRouteTimestamp(anchor.at);
    if (anchorTime == null) {
      i += 1;
      continue;
    }

    let j = i + 1;
    let lastTime = anchorTime;
    let sumLat = anchor.lat;
    let sumLng = anchor.lng;
    let count = 1;

    while (j < points.length) {
      const next = points[j]!;
      const nextTime = parseRouteTimestamp(next.at);
      if (nextTime == null) break;

      sumLat += next.lat;
      sumLng += next.lng;
      count += 1;
      const centroidLat = sumLat / count;
      const centroidLng = sumLng / count;
      const spreadM = haversineMeters(centroidLat, centroidLng, next.lat, next.lng);

      if (spreadM > radiusM) {
        count -= 1;
        sumLat -= next.lat;
        sumLng -= next.lng;
        break;
      }

      lastTime = nextTime;
      j += 1;
    }

    const durationMs = lastTime - anchorTime;
    if (durationMs >= minDurationMs && j > i + 1) {
      const cluster = points.slice(i, j);
      const lat = cluster.reduce((sum, point) => sum + point.lat, 0) / cluster.length;
      const lng = cluster.reduce((sum, point) => sum + point.lng, 0) / cluster.length;
      const endedAt = points[j - 1]!.at;
      if (anchor.at && endedAt) {
        stops.push({
          lat,
          lng,
          startedAt: anchor.at,
          endedAt,
          durationMinutes: Math.max(1, Math.round(durationMs / 60_000)),
        });
      }
    }

    i = j > i + 1 ? j : i + 1;
  }

  return stops;
}

function detectGapStops(
  points: CartrackRoutePoint[],
  minDurationMs: number,
  radiusM: number,
): FleetRouteStop[] {
  const stops: FleetRouteStop[] = [];

  for (let i = 0; i < points.length - 1; i++) {
    const current = points[i]!;
    const next = points[i + 1]!;
    const startTime = parseRouteTimestamp(current.at);
    const endTime = parseRouteTimestamp(next.at);
    if (startTime == null || endTime == null || endTime <= startTime) continue;
    const durationMs = endTime - startTime;
    if (durationMs < minDurationMs) continue;
    const distM = haversineMeters(current.lat, current.lng, next.lat, next.lng);
    if (distM > radiusM) continue;
    if (!current.at || !next.at) continue;
    stops.push({
      lat: (current.lat + next.lat) / 2,
      lng: (current.lng + next.lng) / 2,
      startedAt: current.at,
      endedAt: next.at,
      durationMinutes: Math.max(1, Math.round(durationMs / 60_000)),
    });
  }

  return stops;
}

function mergeFleetStops(stops: FleetRouteStop[]): FleetRouteStop[] {
  const sorted = [...stops].sort(
    (a, b) => (parseRouteTimestamp(a.startedAt) ?? 0) - (parseRouteTimestamp(b.startedAt) ?? 0),
  );
  const merged: FleetRouteStop[] = [];

  for (const stop of sorted) {
    const startMs = parseRouteTimestamp(stop.startedAt);
    const endMs = parseRouteTimestamp(stop.endedAt);
    if (startMs == null || endMs == null) continue;

    const prev = merged[merged.length - 1];
    if (prev) {
      const prevEndMs = parseRouteTimestamp(prev.endedAt);
      if (prevEndMs != null) {
        const overlap = startMs <= prevEndMs + 60_000;
        const nearby =
          haversineMeters(prev.lat, prev.lng, stop.lat, stop.lng) <= 200;
        if (overlap && nearby) {
          if (endMs > prevEndMs) prev.endedAt = stop.endedAt;
          prev.durationMinutes = Math.max(
            1,
            Math.round(((parseRouteTimestamp(prev.endedAt) ?? endMs) - (parseRouteTimestamp(prev.startedAt) ?? startMs)) / 60_000),
          );
          prev.lat = (prev.lat + stop.lat) / 2;
          prev.lng = (prev.lng + stop.lng) / 2;
          continue;
        }
      }
    }

    merged.push({ ...stop });
  }

  return merged;
}

/** Derive day start/end, trip legs (GPS segments), and stops ≥5 min — excludes GPS gap periods. */
export function analyzeFleetRoute(
  points: CartrackRoutePoint[],
  options?: { minStopMinutes?: number; stopRadiusMeters?: number },
): FleetRouteAnalysis {
  const minDurationMs = (options?.minStopMinutes ?? 5) * 60_000;
  const radiusM = options?.stopRadiusMeters ?? 150;

  const timed = points.filter((point) => parseRouteTimestamp(point.at) != null);
  if (timed.length === 0) {
    return { dayStart: null, dayEnd: null, trips: [], stops: [] };
  }

  const segments = splitRouteSegments(timed);
  const trips: FleetRouteTrip[] = segments
    .filter((segment) => segment[0]?.at && segment[segment.length - 1]?.at)
    .map((segment) => ({
      startedAt: segment[0]!.at!,
      endedAt: segment[segment.length - 1]!.at!,
      start: { lat: segment[0]!.lat, lng: segment[0]!.lng },
      end: { lat: segment[segment.length - 1]!.lat, lng: segment[segment.length - 1]!.lng },
    }));

  const clusterStops = segments.flatMap((segment) =>
    detectStopsInSegment(segment, minDurationMs, radiusM),
  );
  const gapStops = segments.flatMap((segment) =>
    detectGapStops(segment, minDurationMs, radiusM),
  );
  const stops = mergeFleetStops([...clusterStops, ...gapStops]);

  const first = timed[0]!;
  const last = timed[timed.length - 1]!;

  return {
    dayStart: first.at ? { lat: first.lat, lng: first.lng, at: first.at } : null,
    dayEnd: last.at ? { lat: last.lat, lng: last.lng, at: last.at } : null,
    trips,
    stops,
  };
}

export function formatRouteTimeOnly(value: string): string {
  const parsed = parseRouteTimestamp(value);
  if (parsed == null) return value;
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Singapore',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  }).format(new Date(parsed));
}

export function formatRouteLocation(lat: number, lng: number): string {
  return `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
}

export function fleetStopDueDateIso(startedAt: string): string {
  const parsed = parseRouteTimestamp(startedAt);
  if (parsed == null) return new Date().toISOString();
  return new Date(parsed).toISOString();
}
